/**
 * The Service module's route table (CLAUDE.md §8.14).
 *
 * `src/router/index.ts` is shared by every module, so this file declares the
 * records and the router spreads `SERVICE_ROUTES` in.
 *
 * Two decisions are worth stating:
 *
 *  - Every `:id` is constrained to `\d+`, so `/service/tickets` and
 *    `/service/customer-assets` can never be parsed as record ids.
 *  - There is **no SLA-policy screen here.** A ticket's deadlines are computed
 *    by `internal/crm`, the policies are configured through CRM's own endpoints,
 *    and `/crm/sla-policies` already edits them. A second screen over the same
 *    table is the "two implementations that drift" outcome `internal/service`'s
 *    own doc comment refuses; the ticket screen links across instead.
 */

import type { RouteRecordRaw } from 'vue-router'

import { SERVICE_PERMISSIONS } from '@/modules/service/permissions'

export const SERVICE_ROUTES: RouteRecordRaw[] = [
  {
    path: '/service/tickets',
    name: 'service-tickets',
    component: () => import('@/modules/service/TicketsListView.vue'),
    meta: { titleKey: 'service.routeTitles.tickets', permission: SERVICE_PERMISSIONS.ticketRead },
  },
  {
    path: '/service/tickets/:id(\\d+)',
    name: 'service-ticket',
    component: () => import('@/modules/service/TicketView.vue'),
    meta: { titleKey: 'service.routeTitles.ticket', permission: SERVICE_PERMISSIONS.ticketRead },
  },

  {
    path: '/service/customer-assets',
    name: 'service-customer-assets',
    component: () => import('@/modules/service/CustomerAssetsListView.vue'),
    meta: {
      titleKey: 'service.routeTitles.customerAssets',
      permission: SERVICE_PERMISSIONS.customerAssetRead,
    },
  },
  {
    path: '/service/customer-assets/:id(\\d+)',
    name: 'service-customer-asset',
    component: () => import('@/modules/service/CustomerAssetView.vue'),
    meta: {
      titleKey: 'service.routeTitles.customerAsset',
      permission: SERVICE_PERMISSIONS.customerAssetRead,
    },
  },

  {
    path: '/service/pm-schedules',
    name: 'service-pm-schedules',
    component: () => import('@/modules/service/PmSchedulesView.vue'),
    meta: {
      titleKey: 'service.routeTitles.pmSchedules',
      permission: SERVICE_PERMISSIONS.pmScheduleRead,
    },
  },

  {
    path: '/service/assignment-rules',
    name: 'service-assignment-rules',
    component: () => import('@/modules/service/AssignmentRulesView.vue'),
    meta: {
      titleKey: 'service.routeTitles.assignmentRules',
      permission: SERVICE_PERMISSIONS.assignmentRuleManage,
    },
  },

  {
    path: '/service/reports',
    name: 'service-reports',
    component: () => import('@/modules/service/ReportsView.vue'),
    meta: { titleKey: 'service.routeTitles.reports', permission: SERVICE_PERMISSIONS.reportRead },
  },

  {
    path: '/service/settings',
    name: 'service-settings',
    component: () => import('@/modules/service/SettingsView.vue'),
    meta: {
      titleKey: 'service.routeTitles.settings',
      permission: SERVICE_PERMISSIONS.settingsManage,
    },
  },
  /* ------------------------------------------------------- C11 additions */

  {
    path: '/service/work-orders',
    name: 'service-work-orders',
    component: () => import('@/modules/service/WorkOrdersListView.vue'),
    meta: {
      titleKey: 'service.routeTitles.workOrders',
      permission: SERVICE_PERMISSIONS.workOrderRead,
    },
  },
  {
    path: '/service/dispatch',
    name: 'service-dispatch',
    component: () => import('@/modules/service/DispatchBoardView.vue'),
    meta: {
      titleKey: 'service.routeTitles.dispatch',
      permission: SERVICE_PERMISSIONS.workOrderRead,
    },
  },
  {
    path: '/service/work-orders/:id(\\d+)',
    name: 'service-work-order',
    component: () => import('@/modules/service/WorkOrderView.vue'),
    meta: {
      titleKey: 'service.routeTitles.workOrder',
      permission: SERVICE_PERMISSIONS.workOrderRead,
    },
  },
  {
    path: '/service/contracts',
    name: 'service-contracts',
    component: () => import('@/modules/service/ContractsListView.vue'),
    meta: {
      titleKey: 'service.routeTitles.contracts',
      permission: SERVICE_PERMISSIONS.contractRead,
    },
  },
  {
    path: '/service/contracts/:id(\\d+)',
    name: 'service-contract',
    component: () => import('@/modules/service/ContractView.vue'),
    meta: {
      titleKey: 'service.routeTitles.contract',
      permission: SERVICE_PERMISSIONS.contractRead,
    },
  },
  {
    path: '/service/rate-cards',
    name: 'service-rate-cards',
    component: () => import('@/modules/service/RateCardsView.vue'),
    meta: {
      titleKey: 'service.routeTitles.rateCards',
      permission: SERVICE_PERMISSIONS.rateCardRead,
    },
  },
  {
    path: '/service/rate-cards/:id(\\d+)',
    name: 'service-rate-card',
    component: () => import('@/modules/service/RateCardsView.vue'),
    meta: {
      titleKey: 'service.routeTitles.rateCard',
      permission: SERVICE_PERMISSIONS.rateCardRead,
    },
  },
  {
    path: '/service/vans',
    name: 'service-vans',
    component: () => import('@/modules/service/VansView.vue'),
    meta: { titleKey: 'service.routeTitles.vans', permission: SERVICE_PERMISSIONS.vanRead },
  },
  {
    path: '/service/problems',
    name: 'service-problems',
    component: () => import('@/modules/service/ProblemsView.vue'),
    meta: { titleKey: 'service.routeTitles.problems', permission: SERVICE_PERMISSIONS.ticketRead },
  },
  {
    path: '/service/goodwill',
    name: 'service-goodwill',
    component: () => import('@/modules/service/GoodwillView.vue'),
    meta: {
      titleKey: 'service.routeTitles.goodwill',
      permission: SERVICE_PERMISSIONS.workOrderRead,
    },
  },
]

/* ------------------------------------------------------------ own screens */

export const SERVICE_PATHS = {
  tickets: '/service/tickets',
  ticket: (id: number) => `/service/tickets/${id}`,
  customerAssets: '/service/customer-assets',
  customerAsset: (id: number) => `/service/customer-assets/${id}`,
  pmSchedules: '/service/pm-schedules',
  assignmentRules: '/service/assignment-rules',
  reports: '/service/reports',
  settings: '/service/settings',

  /* ------------------------------------------------------- C11 additions */

  workOrders: '/service/work-orders',
  workOrder: (id: number) => `/service/work-orders/${id}`,
  dispatch: '/service/dispatch',
  contracts: '/service/contracts',
  contract: (id: number) => `/service/contracts/${id}`,
  rateCards: '/service/rate-cards',
  rateCard: (id: number) => `/service/rate-cards/${id}`,
  vans: '/service/vans',
  problems: '/service/problems',
  goodwill: '/service/goodwill',
} as const

/** One customer's open tickets — the hop from a ticket to their others. */
export function ticketsForCustomer(customerId: number): string {
  return `${SERVICE_PATHS.tickets}?customerId=${customerId}`
}

/** Tickets awaiting billing — the queue a service manager works from. */
export function unbilledTicketsRoute(): string {
  return `${SERVICE_PATHS.tickets}?invoiceStatus=unbilled`
}

/* --------------------------------------------------------- links elsewhere */

/** The AR invoice a billed ticket produced. `internal/ar` owns this URL. */
export function arInvoiceRoute(invoiceId: number): string {
  return `/ar/invoices/${invoiceId}`
}

/** The `internal/ar` customer a ticket is raised against. */
export function customerRoute(customerId: number): string {
  return `/ar/customers/${customerId}`
}

/**
 * The SLA policies screen — `internal/crm`'s, not this module's.
 *
 * A ticket's deadlines are CRM's arithmetic over CRM's policies, and this module
 * deliberately does not re-expose them under its own paths.
 */
export const SLA_POLICIES_ROUTE = '/crm/sla-policies'

/** The underlying case, for anyone who wants CRM's own view of it. */
export function caseRoute(caseId: number): string {
  return `/crm/cases/${caseId}`
}

/** The item behind a consumed part. `internal/inventory` owns this URL. */
export function itemRoute(itemId: number): string {
  return `/inventory/items/${itemId}`
}

/**
 * The knowledge-base article a known error was written up as. CRM owns it.
 *
 * A problem that has become a known error IS an article — there is no second
 * copy of the text in this module, because two places to edit one answer is how
 * a customer is told two different things.
 */
export function kbArticleRoute(articleId: number): string {
  return `/crm/kb/${articleId}`
}

/**
 * Counting a van's stock is a stock count, and `internal/inventory` owns count
 * sessions. The van screen links here rather than offering a second counting UI
 * over the same ledger (C11 §5.5).
 */
export function countSessionsRoute(warehouseId: number): string {
  return `/inventory/cycle-counts?warehouseId=${warehouseId}`
}
